import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';

import { IonContent, IonInfiniteScroll, IonSearchbar, PopoverController } from '@ionic/angular';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { TagsContext } from 'src/app/task-process/shared/tags-context.class';
import { environment } from 'src/environments/environment';
import { StringSearchService } from '../../../services/string-search.service';
import { Tag, TagGroup } from '../../../models/misc.model';
import { AbstractModalWithResult } from '../../abstract-modal-with-result';

@Component({
  selector: 'app-select-tag-popover',
  templateUrl: './select-tag-popover.component.html',
  styleUrls: ['./select-tag-popover.component.scss'],
})
export class SelectTagPopoverComponent extends AbstractModalWithResult<Tag> implements OnInit, AfterViewInit {
  static modalId = 'SelectTagPopoverComponent';

  @Input() group: TagGroup;
  @Input() hideSelection = false;
  @Input() tagsContext: TagsContext;

  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;
  @ViewChild(IonContent) ionContent: IonContent;
  @ViewChild(IonSearchbar) ionSearchbar: IonSearchbar;

  visibleTags: Tag[]; // A tag is visible if it doesn't depend upon another tag that is not set to the required value
  filteredTags: Tag[]; // A tag is in this array if it matches by the current search term
  displayedTags: Tag[]; // A tag is displayed if it is in the first N elements of the filteredTags
  // or if it has been added by an infinite scroll trigger

  shouldDisplaySearchField: boolean;

  constructor(
    popoverController: PopoverController,
    private stringSearchService: StringSearchService,
    private alcmeonContext: AlcmeonContextService
  ) {
    super(popoverController);
  }

  getModalId() {
    return SelectTagPopoverComponent.modalId;
  }

  ngOnInit() {
    this.visibleTags = this.group.tags.filter(tag => this.isTagVisible(tag));
    for (const tag of this.visibleTags) {
      tag.nameForSearch = this.stringSearchService.removeDiacriticsThenLowerCase(tag.name, this.alcmeonContext.lang);
    }
    this.onSearchTermChange({ target: { value: '' } }); // trigger building filteredTags and displayedTags
    this.shouldDisplaySearchField = !environment.isNativeApp && this.visibleTags.length > 10;
  }

  ngAfterViewInit() {
    // can't move the content of ngOnInit here as it would trigger the infamous ExpressionChangedAfterItHasBeenCheckedError,
    // but can't move this code to ngOnInit either as the @ViewChild query hasn't been exectuted yet there
    this.updateInifinteScrollEnabled();

    // Wait until the popover animation has completed to set the focus
    setTimeout(() => {
      this.ionSearchbar?.setFocus();
    }, 500);
  }

  selectTag(tag) {
    super.ok(tag);
  }

  onSearchTermChange(event) {
    const term = this.stringSearchService.removeDiacriticsThenLowerCase(event.target.value, this.alcmeonContext.lang);
    this.filteredTags = this.removeEmptyCategories(
      this.visibleTags.filter(tag => !tag.nameForSearch || tag.nameForSearch.includes(term))
    );
    this.displayedTags = this.filteredTags.splice(0, 200);
    this.scrollToTop();
    this.updateInifinteScrollEnabled();
  }

  onEnterPressed() {
    if (this.displayedTags.length === 1) {
      this.selectTag(this.displayedTags[0]);
    }
  }

  onInfiniteScroll() {
    this.displayedTags.push(...this.filteredTags.splice(0, 200));
    this.infiniteScroll.complete();
    this.updateInifinteScrollEnabled();
  }

  private updateInifinteScrollEnabled() {
    if (this.infiniteScroll) {
      this.infiniteScroll.disabled = this.filteredTags.length === 0;
    }
  }

  private scrollToTop() {
    if (this.ionContent) {
      this.ionContent.scrollToTop(0);
    }
  }

  private isTagVisible(tag: Tag): boolean {
    return this.tagsContext.isTagVisible(tag);
  }

  private removeEmptyCategories(tags: Tag[]) {
    const result = [];
    for (let i = 0; i < tags.length; i++) {
      const tag = tags[i];
      if (!tag.isCategory || (i < tags.length - 1 && !tags[i + 1].isCategory)) {
        result.push(tag);
      }
    }
    return result;
  }
}
